/**
 * Sort choices for the My Cases task inbox (PTV2-1321).
 *
 * Sorting is currently applied client-side to the loaded rows — the backend's
 * `sort` param proved to be a no-op, so the FE owns ordering for now. This
 * module is the single source of truth linking each UI option to its
 * comparator, so the `<select>` and the ordering can't drift.
 */

import { PRIORITY_ORDER } from '@/features/patient-record/components/panels/TasksPanel/taskHelpers';
import type { PatientTaskPriority } from '@/features/patient-record/types';

export type TaskSortField = 'dueDate' | 'priority' | 'taskCount';
export type TaskSortDirection = 'asc' | 'desc';

export interface TaskSortOption {
  /** Stable id used as the `<option>` value and i18n key suffix. */
  readonly id: string;
  readonly field: TaskSortField;
  readonly direction: TaskSortDirection;
  /** Only meaningful when grouping (a group has a task count; a single task doesn't). */
  readonly groupedOnly?: boolean;
}

const DUE_DATE_ASC: TaskSortOption = { id: 'dueDate-asc', field: 'dueDate', direction: 'asc' };

export const TASK_SORT_OPTIONS: readonly TaskSortOption[] = [
  DUE_DATE_ASC,
  { id: 'dueDate-desc', field: 'dueDate', direction: 'desc' },
  { id: 'priority-desc', field: 'priority', direction: 'desc' },
  { id: 'priority-asc', field: 'priority', direction: 'asc' },
  { id: 'taskCount-desc', field: 'taskCount', direction: 'desc', groupedOnly: true },
  { id: 'taskCount-asc', field: 'taskCount', direction: 'asc', groupedOnly: true },
];

export const DEFAULT_TASK_SORT_ID = DUE_DATE_ASC.id;

export function findTaskSortOption(id: string): TaskSortOption {
  return TASK_SORT_OPTIONS.find((option) => option.id === id) ?? DUE_DATE_ASC;
}

/** Options offered for a view — the task-count sort only makes sense when grouping. */
export function sortOptionsForView(grouped: boolean): readonly TaskSortOption[] {
  return grouped ? TASK_SORT_OPTIONS : TASK_SORT_OPTIONS.filter((o) => o.groupedOnly !== true);
}

/** True when a sort id only applies to the grouped view (so the list must reset off it). */
export function isGroupOnlySort(id: string): boolean {
  return findTaskSortOption(id).groupedOnly === true;
}

interface SortableTask {
  readonly dueDate: string | null;
  readonly priority: PatientTaskPriority;
}

/**
 * Comparator for the chosen sort option. Due-date sorts keep empty dates last
 * regardless of direction; priority sorts run over the shared severity rank
 * (`priority-desc` = most urgent first).
 */
export function compareTasksBy(option: TaskSortOption) {
  return (a: SortableTask, b: SortableTask): number => {
    if (option.field === 'priority') {
      const diff = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
      // PRIORITY_ORDER ranks URGENT lowest → ascending rank is most-urgent-first.
      return option.direction === 'desc' ? diff : -diff;
    }
    // Due date: empty always sinks to the bottom.
    if (a.dueDate === null || b.dueDate === null) {
      if (a.dueDate === b.dueDate) return 0;
      return a.dueDate === null ? 1 : -1;
    }
    const delta = new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    return option.direction === 'asc' ? delta : -delta;
  };
}
